import { defaultTheme } from "react-admin";
import { alpha, darken, lighten } from "@mui/material/styles";

const theme = {
  ...defaultTheme,
  palette: {
    ...defaultTheme.palette,
    primary: {
      main: "#1B61D4",
      dark: "#022F8B",
      light: "#95C8DA",
    },
    secondary: {
      main: "#022F8B",
    },
    info: {
      main: "#4D4D4D",
    },
    error: {
      main: "#e31212",
      primary: "#ea0e0eff",
    },
    success: {
      main: "#2e7d32",
      light: "#84ad86ff",
    },
    warning: {
      main: "#ff9800",
      light: "#fbf6dc",
    },
    white: {
      main: "#ffffff",
    },
    homeIcon: {
      main: "transparent",
    },
  },
  components: {
    ...defaultTheme.components,
    // Soft tinted filled inputs with a focus halo (modern UI look); SU keeps react-admin's filled default.
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: "#EBF0F8",
          border: "1px solid #DFDFEE",
          transition: "background-color .18s, border-color .18s, box-shadow .18s",
          "&:hover:not(.Mui-disabled, .Mui-error, .Mui-focused)": {
            backgroundColor: "#E2EAF7",
          },
          "&.Mui-focused": {
            backgroundColor: "#fff",
            borderColor: "#1B61D4",
            boxShadow: `0 0 0 4px ${alpha("#1B61D4", 0.16)}`,
          },
          "&.Mui-error": {
            backgroundColor: "#FDF4F5",
            borderColor: "#e31212",
          },
          "&.Mui-focused.Mui-error": {
            boxShadow: `0 0 0 4px ${alpha("#e31212", 0.14)}`,
          },
          "&.Mui-disabled": {
            backgroundColor: "#F3F3F6",
            borderColor: "#E6E6EC",
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        shrink: {
          fontWeight: 500,
        },
      },
    },
    RaAppBar: {
      styleOverrides: {
        root: { color: "#fff" },
      },
    },
    // RaConfigurable: {
    //   styleOverrides: {
    //     root: { color: "#fff" },
    //   },
    // },
    // RaLoadingIndicator: {
    //   styleOverrides: {
    //     root: { color: "#fff" },
    //   },
    // },
    RaSimpleFormIterator: {
      styleOverrides: {
        root: {
          "& .RaSimpleFormIterator-line": {
            borderLeft: "5px solid #1B61D4",
            backgroundColor: "#F6F6F6",
            borderRadius: "5px",
            padding: "2rem",
            marginBottom: "2.5rem",
          },
          "&  .RaSimpleFormIterator-form ": {
            width: "100%",
            position: "relative",
          },
        },
      },
    },
    RaArrayInput: {
      styleOverrides: {
        root: {
          "& .RaArrayInput-label": {
            color: "#022F8B",
            fontWeight: 700,
            marginBottom: "7px",
            whiteSpace: "pre-wrap",
          },
        },
      },
    },
    RaLabeled: {
      styleOverrides: {
        root: {
          "& .RaLabeled-label": {
            color: "#022F8B",
            fontWeight: 600,
            fontSize: 11,
          },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        sx: { fontSize: 14 },
      },
    },
    RaSidebar: {
      styleOverrides: {
        root: {
          "& .RaSidebar-fixed": {
            position: "fixed",
            height: "92vh",
            overflowY: "auto",
          },
        },
      },
    },
    RaMultiLevelMenu: {
      styleOverrides: {
        root: {
          backgroundColor: "#FCECE8",
        },
      },
    },
    RaMenuItem: {
      styleOverrides: {
        root: {
          color: "#222222",
          whiteSpace: "pre-wrap",
          "& .RaMenuItem-link": {
            whiteSpace: "pre-wrap",
          },
          "& .RaMenuItem-nestedList": {
            paddingLeft: "0px",
          },
          "& .RaMenuItem-itemButton.active": {
            whiteSpace: "pre-wrap",
            paddingLeft: "0.325rem",
            backgroundColor: "#fff",
            borderBottom: "1px solid #FF7200",
          },
          "& .RaMenuItem-active": {
            color: "#fff",
          },
          "& .RaMenuItem-menuIcon": {
            color: "#FF7200",
          },
        },
      },
    },
    MuiTable: {},
    MuiTableHead: {
      defaultProps: {
        sx: {
          backgroundColor: "#222222",
          color: "white",
        },
      },
    },
    RaDatagrid: {
      styleOverrides: {
        root: {
          backgroundColor: "#fff",
          border: "0.5px solid #d6d1d0a3",
          borderRadius: "5px",
          "& .RaDatagrid-headerCell": {
            backgroundColor: "#FF7200",
            // Vertical gradient around the header color: lighter at the top, darker at the bottom.
            backgroundImage: `linear-gradient(180deg, ${lighten("#FF7200", 0.2)} 0%, ${darken("#FF7200", 0.12)} 100%)`,
            color: "#F6F6F6",
            fontWeight: 700,
            fontSize: 15,
            paddingRight: "5px",
            paddingLeft: "10px",
            textAlign: "center",
          },
          "& .MuiTableSortLabel-root.Mui-active": {
            color: "#fff",
          },
          "& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon": {
            color: "#fff",
          },
        },
      },
    },
  },
  homeIconStyle: {
    color: "#fff",
  },
  logoutStyle: {
    flexDirection: "column",
    justifyContent: "center",
    "& .MuiSvgIcon-root": { color: "#FFFFFF", fontSize: "125%" },
    position: "relative",
  },
  RaCreateButton: {
    styleOverrides: {
      root: {
        backgroundColor: "#1B61D4",
        color: "#fff",
        padding: ".3rem 1rem",
        "&:hover": {
          backgroundColor: "#1B61D4",
        },
      },
    },
  },
  RaSaveButton: {
    styleOverrides: {
      root: {
        backgroundColor: "#1B61D4",
        color: "#fff",
        padding: ".3rem 1rem",
        "&:hover": {
          backgroundColor: "#1B61D4",
        },
      },
    },
  },
  RaEditButton: {
    styleOverrides: {
      root: {
        backgroundColor: "#1B61D4",
        color: "#fff",
        padding: ".3rem 1rem",
        "&:hover": {
          backgroundColor: "#1B61D4",
        },
      },
    },
  },
  RaButton: {
    styleOverrides: {
      root: {
        backgroundColor: "#1B61D4",
        color: "#fff",
        padding: ".3rem 1rem",
        "&:hover": {
          backgroundColor: "#1B61D4",
        },
      },
    },
  },
  RaBulkDeleteWithUndoButton: {
    styleOverrides: {
      root: {
        color: "#FFF",
        "&:hover": {
          backgroundColor: "#fff",
          color: "#1B61D4",
        },
      },
    },
  },
};

export default theme;
